// mds-dojo — dojox/gfx/_base — FAITHFUL port of references/dojox/gfx/_base.js.
// The common core Graphics API shared by the gfx renderers: DOM class helpers, font
// metrics (_getFontMeasurements/_getTextBox), text bbox computation, unique-id helper,
// the default shape/stroke/fill/gradient/font prototypes, color/param normalization,
// font string (de)serialization, length conversion, path regexps, and switchTo().
//
// ADAPTATIONS for this package:
//  - AMD dojo ids → relative (`dojo/_base/sniff` → `../../dojo/has`, etc.).
//  - The `dojox.gfx` namespace is anchored to `../main` (dojox) via
//    lang.getObject("gfx", true, dojox) instead of the global-scope form — same object.
//  - `kernel._scopeName` read via cast. No behavioral change; the large pure-doc
//    /*===== =====*/ prototype-description blocks (no executable code) are omitted.
import kernel from '../../dojo/_base/kernel';
import lang from '../../dojo/_base/lang';
import Color from '../../dojo/_base/Color';
import has from '../../dojo/has';
import win from '../../dojo/_base/window';
import arr from '../../dojo/_base/array';
import dom from '../../dojo/dom';
import domConstruct from '../../dojo/dom-construct';
import domGeom from '../../dojo/dom-geometry';
import dojox from '../main';

// module:
//		dojox/gfx

var g: any = lang.getObject("gfx", true, dojox),
	b: any = g._base = {};

// candidates for dojox.style (work on VML and SVG nodes)
g._hasClass = function (/*DomNode*/node: any, /*String*/classStr: any) {
	// summary:
	//		Returns whether or not the specified classes are a portion of the
	//		class list currently applied to the node.
	var cls = node.getAttribute("className");
	return cls && (" " + cls + " ").indexOf(" " + classStr + " ") >= 0;  // Boolean
};
g._addClass = function (/*DomNode*/node: any, /*String*/classStr: any) {
	// summary:
	//		Adds the specified classes to the end of the class list on the
	//		passed node.
	var cls = node.getAttribute("className") || "";
	if (!cls || (" " + cls + " ").indexOf(" " + classStr + " ") < 0) {
		node.setAttribute("className", cls + (cls ? " " : "") + classStr);
	}
};
g._removeClass = function (/*DomNode*/node: any, /*String*/classStr: any) {
	// summary:
	//		Removes classes from node.
	var cls = node.getAttribute("className");
	if (cls) {
		node.setAttribute(
			"className",
			cls.replace(new RegExp('(^|\\s+)' + classStr + '(\\s+|$)'), "$1$2")
		);
	}
};

// candidate for dojox.html.metrics (dynamic font resize handler is not implemented here)

//		derived from Morris John's emResized measurer
b._getFontMeasurements = function () {
	// summary:
	//		Returns an object that has pixel equivilents of standard font
	//		size values.
	var heights: any = {
		'1em': 0, '1ex': 0, '100%': 0, '12pt': 0, '16px': 0, 'xx-small': 0,
		'x-small': 0, 'small': 0, 'medium': 0, 'large': 0, 'x-large': 0,
		'xx-large': 0
	};
	var p, oldStyle;
	if (has("ie")) {
		//	We do a font-size fix if and only if one isn't applied already.
		// NOTE: If someone set the fontSize on the HTML Element, this will kill it.
		oldStyle = win.doc.documentElement.style.fontSize || "";
		if (!oldStyle) {
			win.doc.documentElement.style.fontSize = "100%";
		}
	}

	//		set up the measuring node.
	var div = domConstruct.create("div", {
		style: {
			position: "absolute",
			left: "0",
			top: "-100px",
			width: "30px",
			height: "1000em",
			borderWidth: "0",
			margin: "0",
			padding: "0",
			outline: "none",
			lineHeight: "1",
			overflow: "hidden"
		}
	}, win.body());

	//		do the measurements.
	for (p in heights) {
		div.style.fontSize = p;
		heights[p] = Math.round(div.offsetHeight * 12 / 16) * 16 / 12 / 1000;
	}

	if (has("ie")) {
		// Restore the font to its old style.
		win.doc.documentElement.style.fontSize = oldStyle;
	}
	win.body().removeChild(div);
	return heights; //object
};

var fontMeasurements: any = null;

b._getCachedFontMeasurements = function (recalculate?: any) {
	if (recalculate || !fontMeasurements) {
		fontMeasurements = b._getFontMeasurements();
	}
	return fontMeasurements;
};

// candidate for dojox.html.metrics

var measuringNode: any = null, empty: any = {};
b._getTextBox = function (	/*String*/ text: any,
	/*Object*/ style: any,
	/*String?*/ className?: any) {
	var m, s, al = arguments.length;
	var i, box;
	if (!measuringNode) {
		measuringNode = domConstruct.create("div", {
			style: {
				position: "absolute",
				top: "-10000px",
				left: "0",
				visibility: "hidden"
			}
		}, win.body());
	}
	m = measuringNode;
	// reset styles
	m.className = "";
	s = m.style;
	s.borderWidth = "0";
	s.margin = "0";
	s.padding = "0";
	s.outline = "0";
	// set new style
	if (al > 1 && style) {
		for (i in style) {
			if (i in empty) { continue; }
			s[i] = style[i];
		}
	}
	// set classes
	if (al > 2 && className) {
		m.className = className;
	}
	// take a measure
	m.innerHTML = text;

	if (m.getBoundingClientRect) {
		var bcr = m.getBoundingClientRect();
		box = { l: bcr.left, t: bcr.top, w: bcr.width || (bcr.right - bcr.left), h: bcr.height || (bcr.bottom - bcr.top) };
	} else {
		box = domGeom.getMarginBox(m);
	}
	m.innerHTML = "";
	return box;
};

b._computeTextLocation = function (/*g.defaultTextShape*/textShape: any, /*Number*/width: any, /*Number*/height: any, /*Boolean*/fixHeight: any) {
	var loc: any = {}, align = textShape.align;
	switch (align) {
		case 'end':
			loc.x = textShape.x - width;
			break;
		case 'middle':
			loc.x = textShape.x - width / 2;
			break;
		default:
			loc.x = textShape.x;
			break;
	}
	var c = fixHeight ? 0.75 : 1;
	loc.y = textShape.y - height * c; // **rough** approximation of the ascent...
	return loc;
};
b._computeTextBoundingBox = function (/*shape.Text*/s: any) {
	// summary:
	//		Compute the bbox of the given shape.Text instance.
	if (!g._base._isRendered(s)) {
		return { x: 0, y: 0, width: 0, height: 0 };
	}
	var loc, textShape = s.getShape(),
		font = s.getFont() || g.defaultFont,
		w = s.getTextWidth(),
		h = g.normalizedLength(font.size);
	loc = b._computeTextLocation(textShape, w, h, true);
	return {
		x: loc.x,
		y: loc.y,
		width: w,
		height: h
	};
};
b._isRendered = function (/*Shape*/s: any) {
	var p = s.parent;
	while (p && p.getParent) {
		p = p.parent;
	}
	return p !== null;
};

// candidate for dojo.dom

var uniqueId = 0;
b._getUniqueId = function () {
	// summary:
	//		returns a unique string for use with any DOM element
	var id;
	do {
		id = (kernel as any)._scopeName + "xUnique" + (++uniqueId);
	} while (dom.byId(id));
	return id;
};

// IE10

var touchActionProp = has("pointer-events") ? "touchAction" : has("MSPointer") ? "msTouchAction" : null;
b._fixMsTouchAction = touchActionProp ? function (/*dojox/gfx/shape.Surface*/surface: any) {
	surface.rawNode.style[touchActionProp as any] = "none";
} : function () { };

lang.mixin(g, {
	// summary:
	//		defines constants, prototypes, and utility functions for the core Graphics API

	// default shapes, which are used to fill in missing parameters
	defaultPath: {
		type: "path",
		path: ""
	},
	defaultPolyline: {
		type: "polyline",
		points: []
	},
	defaultRect: {
		type: "rect",
		x: 0,
		y: 0,
		width: 100,
		height: 100,
		r: 0
	},
	defaultEllipse: {
		type: "ellipse",
		cx: 0,
		cy: 0,
		rx: 200,
		ry: 100
	},
	defaultCircle: {
		type: "circle",
		cx: 0,
		cy: 0,
		r: 100
	},
	defaultLine: {
		type: "line",
		x1: 0,
		y1: 0,
		x2: 100,
		y2: 100
	},
	defaultImage: {
		type: "image",
		x: 0,
		y: 0,
		width: 0,
		height: 0,
		src: ""
	},
	defaultText: {
		type: "text",
		x: 0,
		y: 0,
		text: "",
		align: "start",
		decoration: "none",
		rotated: false,
		kerning: true
	},
	defaultTextPath: {
		type: "textpath",
		text: "",
		align: "start",
		decoration: "none",
		rotated: false,
		kerning: true
	},

	// default stylistic attributes
	defaultStroke: {
		type: "stroke",
		color: "black",
		style: "solid",
		width: 1,
		cap: "butt",
		join: 4
	},
	defaultLinearGradient: {
		type: "linear",
		x1: 0,
		y1: 0,
		x2: 100,
		y2: 100,
		colors: [
			{ offset: 0, color: "black" }, { offset: 1, color: "white" }
		]
	},
	defaultRadialGradient: {
		type: "radial",
		cx: 0,
		cy: 0,
		r: 100,
		colors: [
			{ offset: 0, color: "black" }, { offset: 1, color: "white" }
		]
	},
	defaultPattern: {
		type: "pattern",
		x: 0,
		y: 0,
		width: 0,
		height: 0,
		src: ""
	},
	defaultFont: {
		type: "font",
		style: "normal",
		variant: "normal",
		weight: "normal",
		size: "10pt",
		family: "serif"
	},

	getDefault: (function () {
		// summary:
		//		Returns a function used to access default memoized prototype objects (see them defined above).
		var typeCtorCache: any = {};
		// a memoized delegate()
		return function (/*String*/ type: any) {
			var t = typeCtorCache[type];
			if (t) {
				return new t();
			}
			t = typeCtorCache[type] = function () { };
			t.prototype = g["default" + type];
			return new t();
		};
	})(),

	normalizeColor: function (/*dojo/Color|Array|string|Object*/ color: any) {
		// summary:
		//		converts any legal color representation to normalized
		//		dojo/Color object
		return (color instanceof Color) ? color : new (Color as any)(color); // dojo/Color
	},
	normalizeParameters: function (existed: any, update: any) {
		// summary:
		//		updates an existing object with properties from an 'update' object
		var x;
		if (update) {
			var empty: any = {};
			for (x in existed) {
				if (x in update && !(x in empty)) {
					existed[x] = update[x];
				}
			}
		}
		return existed;	// Object
	},
	makeParameters: function (defaults: any, update: any) {
		// summary:
		//		copies the original object, and all copied properties from the
		//		'update' object
		var i: any = null;
		if (!update) {
			// return dojo.clone(defaults);
			return lang.delegate(defaults);
		}
		var result: any = {};
		for (i in defaults) {
			if (!(i in result)) {
				result[i] = lang.clone((i in update) ? update[i] : defaults[i]);
			}
		}
		return result; // Object
	},
	formatNumber: function (x: any, addSpace: any) {
		// summary:
		//		converts a number to a string using a fixed notation
		var val = x.toString();
		if (val.indexOf("e") >= 0) {
			val = x.toFixed(4);
		} else {
			var point = val.indexOf(".");
			if (point >= 0 && val.length - point > 5) {
				val = x.toFixed(4);
			}
		}
		if (x < 0) {
			return val; // String
		}
		return addSpace ? " " + val : val; // String
	},
	// font operations
	makeFontString: function (font: any) {
		// summary:
		//		converts a font object to a CSS font string
		return font.style + " " + font.variant + " " + font.weight + " " + font.size + " " + font.family; // Object
	},
	splitFontString: function (str: any) {
		// summary:
		//		converts a CSS font string to a font object
		var font = g.getDefault("Font");
		var t = str.split(/\s+/);
		do {
			if (t.length < 5) { break; }
			font.style = t[0];
			font.variant = t[1];
			font.weight = t[2];
			var i = t[3].indexOf("/");
			font.size = i < 0 ? t[3] : t[3].substring(0, i);
			var j = 4;
			if (i < 0) {
				if (t[4] == "/") {
					j = 6;
				} else if (t[4].charAt(0) == "/") {
					j = 5;
				}
			}
			if (j < t.length) {
				font.family = t.slice(j).join(" ");
			}
		} while (false);
		return font;	// Object
	},
	// length operations

	// cm_in_pt: Number
	//		points per centimeter (constant)
	cm_in_pt: 72 / 2.54,

	// mm_in_pt: Number
	//		points per millimeter (constant)
	mm_in_pt: 7.2 / 2.54,

	px_in_pt: function () {
		// summary:
		//		returns the current number of pixels per point.
		return g._base._getCachedFontMeasurements()["12pt"] / 12;	// Number
	},

	pt2px: function (len: any) {
		// summary:
		//		converts points to pixels
		return len * g.px_in_pt();	// Number
	},

	px2pt: function (len: any) {
		// summary:
		//		converts pixels to points
		return len / g.px_in_pt();	// Number
	},

	normalizedLength: function (len: any) {
		// summary:
		//		converts any length value to pixels
		if (len.length === 0) { return 0; }
		if (len.length > 2) {
			var px_in_pt = g.px_in_pt();
			var val = parseFloat(len);
			switch (len.slice(-2)) {
				case "px": return val;
				case "pt": return val * px_in_pt;
				case "in": return val * 72 * px_in_pt;
				case "pc": return val * 12 * px_in_pt;
				case "mm": return val * g.mm_in_pt * px_in_pt;
				case "cm": return val * g.cm_in_pt * px_in_pt;
			}
		}
		return parseFloat(len);	// Number
	},

	// pathVmlRegExp: RegExp
	//		a constant regular expression used to split a SVG/VML path into primitive components
	pathVmlRegExp: /([A-Za-z]+)|(\d+(\.\d+)?)|(\.\d+)|(-\d+(\.\d+)?)|(-\.\d+)/g,

	// pathSvgRegExp: RegExp
	//		a constant regular expression used to split a SVG/VML path into primitive components
	pathSvgRegExp: /([A-DF-Za-df-z])|([-+]?\d*[.]?\d+(?:[eE][-+]?\d+)?)/g,

	equalSources: function (a: any, b: any) {
		// summary:
		//		compares event sources, returns true if they are equal
		return a && b && a === b;
	},

	switchTo: function (/*String|Object*/ renderer: any) {
		// summary:
		//		switch the graphics implementation to the specified renderer.
		var ns = typeof renderer == "string" ? g[renderer] : renderer;
		if (ns) {
			// If more options are added, update the docblock at the end of shape.js!
			arr.forEach(["Group", "Rect", "Ellipse", "Circle", "Line",
				"Polyline", "Image", "Text", "Path", "TextPath",
				"Surface", "createSurface", "fixTarget"], function (name: any) {
					g[name] = ns[name];
				});
			if (typeof renderer == "string") {
				g.renderer = renderer;
			} else {
				arr.some(["svg", "vml", "canvas", "canvasWithEvents", "silverlight"], function (r: any) {
					return (g.renderer = g[r] && g[r].Surface === g.Surface ? r : null);
				});
			}
		}
	}
});

export default g; // defaults object api
